import type { ClaimsProps } from '@/components/renters/Claims';
import type { HeroProps } from '@/components/renters/Hero';
import type { CustomersReviewsProps, ProductsProps } from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/renters/OG_FR_EN.png';
import bicyclesImg from '@/static/assets/renters/stuff/lg/bicycles.svg';
import cameraImg from '@/static/assets/renters/stuff/lg/camera.svg';
import type { BasePageConfig } from '../../BasePageConfig';
import { BottomCTAProps } from '@/components/renters/BottomCTA';
import type { CoverageProps } from '@/components/homeowners/coverage/Coverage';
import { GetCoveredProps } from '@/components/renters/GetCovered';
// images
import mobile5 from '@/static/assets/renters/coverage_fr/5-mobile.svg';
import desktop5 from '@/static/assets/renters/coverage_fr/5-desktop.svg';
import mobile4 from '@/static/assets/renters/coverage_fr/4-mobile.svg';
import desktop4 from '@/static/assets/renters/coverage_fr/4-desktop.svg';
import mobile3 from '@/static/assets/renters/coverage_fr/3-mobile.svg';
import desktop3 from '@/static/assets/renters/coverage_fr/3-desktop.svg';
import mobile2 from '@/static/assets/renters/coverage_fr/2-mobile.svg';
import desktop2 from '@/static/assets/renters/coverage_fr/2-desktop.svg';
import mobile1 from '@/static/assets/renters/coverage_fr/1-mobile.svg';
import desktop1 from '@/static/assets/renters/coverage_fr/1-desktop.svg';
import animationConfig from '@/static/assets/renters/RT_Bottom_Illustration.json';

import arrowIndicatorLeft from '@/static/assets/renters/claims/fr/en-fr-left.svg';
import arrowIndicatorRight from '@/static/assets/renters/claims/fr/en-fr-right.svg';
import claimsMobilePhone from '@/static/assets/renters/claims/fr/phone-fr-en.svg';
import phoneTwoConfigFr from '@/static/assets/renters/phone_right_fr.json';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Insurance policies for Renters',
  description: 'Insurance policies for Renters',
  image: [bicyclesImg.src, cameraImg.src],
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '5',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  claims: ClaimsProps;
  products: ProductsProps;
  coverage: GetCoveredProps;
  bottomCTA: BottomCTAProps;
  frCoverage: CoverageProps;
}

export const config: Config = {
  layout: {
    ...sharedConfig['en-FR'].layout,
    headerActions: {
      ...sharedConfig['en-FR'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-FR'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-FR'].layout.headerActions.CTAs.checkOurPrices,
          customUrl: '/onboarding/start?f=1&ownership=renter',
        },
      },
    },
  },
  seo: {
    title: 'Renters Insurance - Fast, Free Quotes Online | Lemonade',
    metaTitle: 'Renters Insurance - Fast, Free Quotes Online | Lemonade',
    description:
      'Renters insurance by Lemonade offers peace of mind at an affordable price. Get your free quote for protection today, starting at €4/month.',
    ogImage: ogImage.src,
    jsonld: [productJsonld],
    uniqueCanonicalUrl: 'https://www.lemonade.com/fr/en/renters-insurance',
  },
  hero: {
    title: 'Renters insurance that\nkeeps your stuff safe',
    subtitle: 'Super fast. Great prices. Big heart.',
    ctaLabel: 'Check our prices',
    trackingLabel: 'renters_hero',
    ctaUrl: '/onboarding/start?f=1&ownership=renter',
    stuffType: 'stuff_fr',
  },
  customersReviews: {
    title: 'Top-rated renters insurance\nin France',
    subtitle:
      'Lemonade has earned 4.9 stars in the App store,\nand is top-rated on <a href="https://fr.trustpilot.com/review/lemonade.com">Truspilot</a>',
    darkBackground: true,
  },
  coverage: {
    title: 'Get covered in as little as 90 seconds',
    subtitle: 'Our AI can activate your policy and protect your stuff instantly.',
  },
  frCoverage: {
    darkBackground: true,
    pt: [45, 95],
    title: 'What does renters insurance actually cover?',
    subtitle:
      ' It protects your personal property and helps cover personal liability costs.\nLemonade Renters has your back when bad luck strikes.',
    data: [
      {
        title: 'If someone\nswipes your bike',
        description: 'Complete Theft coverage pays for your stuff even if it gets stolen outside your home.',
        img: {
          src: {
            lg: desktop1,
            sm: mobile1,
          },
          alt: '',
        },
      },
      {
        title: 'If a water leak\nruins your stuff',
        description: 'We cover your stuff if your washing machine is leaking or if your pipes cause a mess.',
        img: {
          src: {
            lg: desktop2,
            sm: mobile2,
          },
          alt: '',
        },
      },
      {
        title: 'If your stuff\ngoes up in flames',
        description: 'We cover your stuff against fire or smoke if things get a little heated.',
        img: {
          src: {
            lg: desktop3,
            sm: mobile3,
          },
          alt: '',
        },
      },
      {
        title: 'If you need\nLegal Protection',
        description:
          'If you need a lawyer to defend you, we’ll help cover these costs with our Legal Protection Add-on',
        img: {
          src: {
            lg: desktop4,
            sm: mobile4,
          },
          alt: '',
        },
      },
      {
        title: 'If you accidentally cause\ndamage to someone else',
        description:
          "We've got you covered if you're liable for damaging someone else's stuff or causing bodily injury.",
        img: {
          src: {
            lg: desktop5,
            sm: mobile5,
          },
          alt: '',
        },
      },
    ],
  },
  claims: {
    title: 'Claims handled at record speed',
    subtitle: 'No paperwork. No hassle. No waiting.',
    arrowIndicators: {
      left: arrowIndicatorLeft,
      right: arrowIndicatorRight,
    },
    mobileImage: claimsMobilePhone,
    phoneTwoConfig: phoneTwoConfigFr,
    lerpValue: {
      phoneOne: 30,
      phoneTwo: -30,
    },
  },
  bottomCTA: {
    title: 'Starting from 4 € /mo.',
    subtitle: 'Safeguard all your stuff with just a few taps.',
    ctaUrl: '/onboarding/start?f=1&ownership=renter',
    animationConfig,
  },
  products: {
    darkBackground: true,
    title: 'More home products from Lemonade',
    products: [
      {
        title: 'Homeowners',
        subtitle: 'Protection for your stuff, plus your actual home',
        price: 'FROM 10 €/MO',
        product: 'home_eu_owners',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'Homeowners Insurance',
      },
      {
        title: 'Landlords',
        subtitle: 'Coverage for your home while you’re renting it to a tenant',
        price: 'FROM 6 €/MO',
        product: 'landlords_eu',
        ctaLabelDesktop: 'Check our prices',
        ctaLabelMobile: 'Check prices',
        customUrl: '/onboarding/start?f=1&ownership=renter',
        alt: 'Landlords Insurance',
      },
    ],
  },
};
